import {
  AccountSelectContainer,
  CardsContainer,
  ConsultLabel,
  ConsultLabelWarning,
  Container,
  HeaderCredits,
  MobileContainer
} from "./styles";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmPurchase } from "../components/modals/confirm-purchase";
import { GetInTouch } from "../components/modals/get-in-touch";
import { Page } from "../../../components/page-base";
import { NewCreditCard } from "../components/new-card";
import { Input } from "../../../components/inputs";
import permissionsStore from "../../../store/permissions";
import { TAccount, TAccountRes } from "../../accounts/models";
import { AccountsService } from "../../accounts/services";
import { TSelectAccounts } from "../../scenarios/models";
import { useAuthContext, useNavigationContext } from "../../../contexts";
import { ICustomPlan, TPayment } from "../models";
import { CreditService } from "../services";
import { cn } from "@/lib/utils";
import { FreeCredit } from "../components/modals/free-credit";
import { toast } from "react-toastify";
import { useIsMobile } from "@/hooks";
import { SelectCriterio } from "../../../components/modal-template/modal-select-criterio";
import {
  readFromLocalStorage,
  removeFromLocalStorage
} from "@/modules/consult/utils/utils";

export const Credits: React.FC = () => {
  const { role, storageUser } = permissionsStore();
  const hasExecuted = useRef(false);
  const { user } = useAuthContext();
  const { selectedAccountCombo, setSelectedAccountCombo } =
    useNavigationContext();
  const [modal, setModal] = useState<boolean>(false);
  const [modalFreeCredit, setModalFreeCredit] = useState<boolean>(false);
  const [modalCustom, setModalCustom] = useState<boolean>(false);

  const [accountsOptions, setAccountOptions] = useState<TSelectAccounts[]>([]);
  const [credits, setCredits] = useState<number>(0);
  const [customPlan, setCustomPlan] = useState<ICustomPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [modalCriterios, setModalCriterios] = useState<boolean>(false);
  const [creditsDetails, setCreditsDetails] = useState<{
    amount: number;
    price: string;
  }>({
    amount: 0,
    price: ""
  });
  const fetchAllAccounts = async () => {
    const allAccounts: TAccount[] = [];
    let page = 1;
    let total = 0;

    try {
      do {
        const response: TAccountRes | null = await AccountsService.getAccounts(
          page,
          "",
          100
        );
        if (response && response.data && Array.isArray(response.data)) {
          allAccounts.push(...response.data);
        }
        if (response && response.meta) {
          total = response.meta.total;
        } else {
          total = 0;
        }
        page++;
      } while (allAccounts.length < total);
      return allAccounts;
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      throw error;
    }
  };

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const fetchedAccounts = await fetchAllAccounts();
        if (fetchedAccounts.length) {
          if (selectedAccountCombo) {
            //caso o usuário tenha selecionado um usuário no combobox
            const selectedAccount = fetchedAccounts.find(
              (acc) => acc.id === selectedAccountCombo
            );
            if (selectedAccount) setCredits(selectedAccount!.credits);
            setSelectedAccountCombo(selectedAccount!.id);
          } else {
            //provavelmente a página acabou de iniciar e o usuário não selecionou nennhum usuário na combo,
            //chance de ter vindo do wix
            const oldestAccount = fetchedAccounts.reduce((oldest, current) => {
              return new Date(current.createdAt) < new Date(oldest.createdAt)
                ? current
                : oldest;
            });
            setSelectedAccountCombo(oldestAccount.id);
            setCredits(oldestAccount.credits);
          }
          const options = fetchedAccounts.map((acc) => ({
            value: acc.id.toString(),
            label: acc.name
          }));
          setAccountOptions(options);
          if (hasExecuted.current) return;
          const wixPayment = localStorage.getItem("puxa-capivara-card-payment");
          if (!wixPayment) {
            hasExecuted.current = true;
            return;
          }
          if (wixPayment === "5") {
            toggleModalCustom();
          } else {
            const { price, amount } = parseCreditsPayment(wixPayment);
            setCreditsDetails({ amount: amount, price: price });
          }
          hasExecuted.current = true;
        }
      } catch (error) {
        console.error("Error loading accounts:", error);
      }
    };
    loadAccounts();
    // eslint-disable-next-line
  }, [selectedAccountCombo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      const firstOwnerLogin = readFromLocalStorage("firstOwnerLogin");
      if (firstOwnerLogin) {
        toggleModalCriterios();
        toggleModalFreeCredits();
        removeFromLocalStorage("firstOwnerLogin");
      }
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (selectedAccountCombo && checkCredits(creditsDetails)) {
      setModal((prev) => !prev);
      localStorage.removeItem("puxa-capivara-card-payment");
    }
  }, [creditsDetails]);

  const checkCredits = (currentCredits: TPayment) => {
    if (currentCredits.amount > 0 && currentCredits.price !== "") {
      return true;
    } else {
      return false;
    }
  };

  const parseCreditsPayment = (wixPayment: string): TPayment => {
    switch (wixPayment) {
      case "1":
        return { price: "30", amount: 1 };
      case "2":
        return { price: "200", amount: 10 };
      case "3":
        return { price: "300", amount: 20 };
      case "4":
        return { price: "500", amount: 50 };
      default:
        return { price: "0", amount: 0 };
    }
  };

  const toggleModal = () => {
    if (selectedAccountCombo) {
      setModal((prev) => !prev);
      localStorage.removeItem("puxa-capivara-card-payment");
    } else {
      toast.error("Conta não selecionada");
    }
  };

  const toggleModalCriterios = () => {
    setModalCriterios((prev) => !prev);
    localStorage.removeItem("puxa-capivara-card-payment");
  };
  const toggleModalCustom = () => {
    setModalCustom((prev) => !prev);
    localStorage.removeItem("puxa-capivara-card-payment");
  };
  const toggleModalFreeCredits = () => {
    setModalFreeCredit((prev) => !prev);
  };
  const parseCredits = (id: string) => {
    setCredits(accountsOptions.find((acc) => acc.value === id)?.payload || 0);
  };

  const fetchPlan = async () => {
    if (storageUser && storageUser.planId) {
      const response = await CreditService.showPlan({
        planId: storageUser?.planId
      });
      if (response.success) {
        setCustomPlan({
          minCredits: response.data!.minCredits!,
          price: Number(response.data!.price)
        });
      }
    } else {
      setCustomPlan(null);
    }
  };

  useEffect(() => {
    if (selectedAccountCombo) {
      fetchPlan();
    }
    // eslint-disable-next-line
  }, [selectedAccountCombo]);

  const handleClickCustomCreditCard = () => {
    const minCredits = customPlan?.minCredits ?? 75;
    const price = customPlan?.price ? customPlan.price.toString() : "8";
    setCreditsDetails({ amount: minCredits, price: price });
  };

  return (
    <Page.Root>
      <ConfirmPurchase
        modal={modal}
        toggleModal={toggleModal}
        selectedAccount={selectedAccountCombo!}
        customPlan={customPlan}
        creditsDetailsParam={creditsDetails}
      />
      <FreeCredit
        modal={modalFreeCredit}
        toggleModal={toggleModalFreeCredits}
      />
      <SelectCriterio
        modal={modalCriterios}
        toggleModal={toggleModalCriterios}
        selectedAccountCombo={selectedAccountCombo!}
      />
      {isLoading && (
        <div className="flex items-center justify-center h-screen w-full bg-black text-white">
          <p className="text-xl animate-pulse">Carregando...</p>
        </div>
      )}
      {!isLoading && (
        <>
          <GetInTouch modal={modalCustom} toggleModal={toggleModalCustom} />
          <Page.Header
            title="Créditos"
            key="credits"
            controlElement={[
              !isMobile && role == "owner" && (
                <AccountSelectContainer key="account-select">
                  <span className="label">Conta para ser creditada:</span>
                  <Input.Select
                    onChange={(newValue) => {
                      setSelectedAccountCombo(Number(newValue));
                      parseCredits(newValue.toString());
                    }}
                    selectedValue={selectedAccountCombo?.toString()}
                    options={accountsOptions}
                  />
                </AccountSelectContainer>
              ),
              user?.paymentType == "PREPAID" && (
                <div
                  style={{
                    width: "inherit",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end"
                  }}
                >
                  <ConsultLabel qty={credits}>
                    Consultas restantes: ({credits})
                  </ConsultLabel>
                  {credits == 0 && (
                    <ConsultLabelWarning onClick={() => navigate("/credits")}>
                      Recarregue seus créditos
                    </ConsultLabelWarning>
                  )}
                </div>
              )
            ].filter(Boolean)}
          />
          <MobileContainer>
            {role == "owner" && (
              <>
                <AccountSelectContainer key="account-select">
                  <span className="label">Destino do crédito:</span>
                  <Input.Select
                    onChange={(newValue) => {
                      setSelectedAccountCombo(Number(newValue));
                      parseCredits(newValue.toString());
                    }}
                    selectedValue={selectedAccountCombo?.toString()}
                    options={accountsOptions}
                  />
                </AccountSelectContainer>
              </>
            )}
          </MobileContainer>
          <Container>
            <HeaderCredits className="laptop:!mb-0 mobile:mt-10">
              <h2
                className={cn(
                  { "!text-[36px]": !!customPlan },
                  "mobile:!text-[14px]"
                )}
              >
                {customPlan
                  ? "Você tem um plano personalizado!"
                  : "Consulte à vontade!"}
              </h2>
              <h1
                className={cn({ hidden: !!customPlan }, "mobile:!text-[28px]")}
              >
                Escolha pacotes para economizar.
              </h1>
              <span className="mobile:!text-[14px]">
                Forma de pagamento via PIX
              </span>
            </HeaderCredits>
            <CardsContainer key={customPlan ? "custom" : "default"}>
              {!customPlan && (
                <div className="flex flex-col mr-10 mobile:mr-0 mobile:mb-5 mt-4 items-center justify-center">
                  <div className="border-2 leading-4 w-[170px] mobile:w-[130px] animate-zoom laptop:w-[130px] flex items-center justify-center mb-6 border-borderCreditsDiv text-white bg-black rounded-[18px] h-14">
                    Indicado para <br />
                    Pessoa Fisica
                  </div>
                  <NewCreditCard
                    colorTitle="black"
                    colorBody="black"
                    leftColor="#E3CBA7"
                    backGroundColor="#D1BD9C"
                    numberOfQueries={1}
                    price={30}
                    onClick={() => {
                      setCreditsDetails({ amount: 1, price: "30" });
                    }}
                  />
                </div>
              )}

              <div className="relative flex flex-col text-center items-center justify-center">
                {!customPlan && (
                  <div className="relative mt-10 flex items-center justify-center border-t-2 w-[80%] border-borderCreditsDiv rounded-full h-14">
                    <div className="absolute -top-7 leading-4 mobile:-top-7 bg-black text-white border-borderCreditsDiv border-2 rounded-[18px] p-3 mobile:px-3 animate-zoom">
                      Indicado <br className="hidden mobile:flex" />
                      para empresas
                    </div>
                  </div>
                )}
                <div
                  className={cn(
                    "flex items-center justify-center gap-5 mobile:flex-wrap",
                    {
                      "mobile:!-mt-4": !!customPlan
                    }
                  )}
                >
                  {!customPlan && (
                    <>
                      <NewCreditCard
                        colorTitle="white"
                        colorBody="black"
                        leftColor="#C7A25C"
                        backGroundColor="#B09151"
                        numberOfQueries={10}
                        price={20}
                        onClick={() => {
                          setCreditsDetails({
                            amount: 10,
                            price: "20"
                          });
                        }}
                      />
                      <NewCreditCard
                        colorTitle="white"
                        colorBody="black"
                        leftColor="#C7A25C"
                        backGroundColor="#B09151"
                        numberOfQueries={20}
                        price={15}
                        onClick={() => {
                          setCreditsDetails({
                            amount: 20,
                            price: "15"
                          });
                        }}
                      />
                      <NewCreditCard
                        colorTitle="white"
                        colorBody="black"
                        leftColor="#C7A25C"
                        backGroundColor="#B09151"
                        numberOfQueries={50}
                        price={10}
                        onClick={() => {
                          setCreditsDetails({
                            amount: 50,
                            price: "10"
                          });
                        }}
                      />
                    </>
                  )}
                  <NewCreditCard
                    colorTitle="white"
                    className={cn({ "mt-10": customPlan })}
                    colorBody="#C0A15B"
                    leftColor="#241E20"
                    backGroundColor="#2E2528"
                    numberOfQueries={customPlan?.minCredits || 75}
                    borderCustomer={true}
                    price={customPlan?.price || 8}
                    customPlan={customPlan}
                    onClick={handleClickCustomCreditCard}
                  />
                </div>
              </div>
            </CardsContainer>
          </Container>
        </>
      )}
    </Page.Root>
  );
};
